import autobind from 'autobind-decorator';
import { asyncAction } from 'mobx-utils';
import ClockService from '../services/game/clock';
import { Url } from '../constants/environment';
import axios from 'axios';

const TIMEZONE = 'Asia/Seoul';

@autobind
class ClockStore {
  time = null;
  useTimer = false;
  addSecondCount = 0;
  addSecondTimer = null;
  plusSecond = 0;
  autoStopTimer = null;

  constructor() {}

  /**
   * 타이머 정지
   */
  stop() {
    clearTimeout(this.addSecondTimer);
    this.time = null;
    this.useTimer = false;
    this.plusSecond = 0;
  }

  /**
   * 저장된 시간에 1초 더함
   */
  addSecond() {
    if (!this.time) {
      return false;
    }

    this.addSecondCount++;

    //현재시간에 1초 더함
    this.time.setSeconds(this.time.getSeconds() + 1);

    //10초에 한번
    if (this.addSecondCount >= 10) {
      this.addSecondCount = 0;
      this.setTime();
    } else {
      clearTimeout(this.addSecondTimer);
      this.addSecondTimer = setTimeout(this.addSecond, 1000);
    }
  }

  /**
   * 서버와 통신해 현재시작 조회
   * @returns {Generator<Promise<*>, void, ?>}
   */
  @asyncAction
  *setTime() {
    const res = yield ClockService.getTime();

    if (res.headers.date) {
      this.time = new Date(res.headers.date);
      const offsetHours = this.time.getTimezoneOffset() / 60;
      this.time.setHours(this.time.getHours() + offsetHours + 9);
      this.time.setSeconds(this.time.getSeconds() + this.plusSecond);
      this.addSecondCount = 0;

      if (this.useTimer) {
        this.addSecond();
      }
    }
  }

  /**
   * 현재 초 조회
   * @returns {*}
   */
  getNowSecond() {
    return (
      this.time.getHours() * 3600 +
      this.time.getMinutes() * 60 +
      this.time.getSeconds()
    );
  }

  /**
   * 게임 회차 조회
   * @param addSecond
   * @param cycleSecond
   * @param maxRound
   * @returns {{currentRound: (*|number), nextRound: number}}
   */
  async getGameRound(addSecond, cycleSecond, maxRound) {
    if (!this.time) {
      await this.setTime();
    }

    const nowSecond = this.getNowSecond() + addSecond;

    let nextRound = Math.ceil(nowSecond / cycleSecond);
    nextRound = maxRound < nextRound ? 1 : nextRound;

    let currentRound = nextRound - 1;
    currentRound = currentRound === 0 ? maxRound : currentRound;


    let remainTime =
      cycleSecond * nextRound - (this.getNowSecond() + addSecond);
    let second =
      remainTime % 60 < 10 ? '0' + (remainTime % 60) : remainTime % 60;
    remainTime = Math.floor(remainTime / 60) + ':' + second;

    return {
      nextRound: nextRound,
      currentRound: currentRound,
      remainTime: remainTime
    };
  }

  /**
   * Get dist.json URL for game (used for reference, actual API call uses game_type)
   * @param {string} gameName - Game name from URL
   * @returns {string|null} - dist.json URL or null if not found
   */
  getDistUrl(gameName) {
    const distUrlMap = {
      // Runningball games
      'double_dragon': '/data/minigame/rball/double_dragon/dist.json',
      'space8': '/data/minigame/rball/space8/dist.json',
      'runningball5_6': '/data/minigame/rball/runningball5/ball6/dist.json',
      'runningball5_4': '/data/minigame/rball/runningball5/ball4/dist.json',
      'runningball3': '/data/minigame/rball/runningball3/dist.json',
      // Powerball games
      'powerball5': '/data/minigame/nball/powerball5/dist.json',
      'powerball3': '/data/minigame/nball/powerball3/dist.json',
      'powerball': '/data/minigame/redball/powerball/dist.json', // redball powerball
      // Powerladder games
      'powerladder5': '/data/minigame/nball/powerladder5/dist.json',
      'powerladder3': '/data/minigame/nball/powerladder3/dist.json',
      'powerladder': '/data/minigame/redball/powerladder/dist.json', // redball powerladder
      // Dari games (ladder series)
      'ladder': '/data/json/games/theme/ladder/dist.json',
      'daridari': '/data/json/games/theme/daridari/dist.json',
      'speedladder': '/data/json/games/theme/speed_ladder/dist.json',
    };

    return distUrlMap[gameName] || null;
  }

  /**
   * Get end maintenance hour in KST
   * Maintenance ends at 9am PDT (March-November) or 10am PST (November-March)
   * 9am PDT (UTC-7) = 16:00 UTC = 01:00 KST next day
   * 10am PST (UTC-8) = 18:00 UTC = 03:00 KST next day
   * @returns {number} Hour in KST (1 for DST, 3 for non-DST)
   */
  getEndMaintenanceHourKST() {
    const now = new Date();
    
    // Get LA time to check DST
    const laTimeStr = now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' });
    const laTime = new Date(laTimeStr);
    const month = laTime.getMonth();
    const day = laTime.getDate();
    const year = laTime.getFullYear();
    
    // Check if DST: second Sunday of March to first Sunday of November
    // Simplified: March (2) to October (9) is usually DST
    let isDST = false;
    if (month >= 2 && month <= 9) {
      // March to October: check if before first Sunday of November
      if (month < 10) {
        isDST = true;
      } else if (month === 10) { // November
        // Check if before first Sunday
        const firstDay = new Date(year, 10, 1);
        const firstSunday = 7 - firstDay.getDay();
        isDST = day < firstSunday;
      }
    }
    
    // Maintenance ends at 9am PDT or 10am PST
    // Convert to KST: 9am PDT = 1am KST, 10am PST = 3am KST
    return isDST ? 1 : 3;
  }

  /**
   * 런닝볼계열 회차 조회 (matching backend PHP logic - fallback calculation)
   * @param {number} cycleMinutes - Cycle in minutes (2, 3, or 5)
   * @param {number} maxRound - Maximum rounds per day
   * @param {number} maintenanceRounds - Number of maintenance rounds to subtract
   * @returns {Promise<{currentRound: number, nextRound: number, remainTime: string}>}
   */
  async getRunningballRound(cycleMinutes, maxRound, maintenanceRounds) {
    if (!this.time) {
      await this.setTime();
    }

    // Get KST time
    const nowKST = new Date(this.time);
    const hourKST = nowKST.getHours();
    const minuteKST = nowKST.getMinutes();
    const secondKST = nowKST.getSeconds();

    // Calculate timeSeconds in KST (matching backend getCurrentRoundByMinutes)
    const timeSeconds = (hourKST * 3600) + (minuteKST * 60) + secondKST;
    
    // Calculate round using ceil (matching backend)
    let round = Math.ceil(timeSeconds / (60 * cycleMinutes));

    // Get end maintenance hour in KST
    const endMaintenanceHourKST = this.getEndMaintenanceHourKST();

    // Subtract maintenance rounds if after maintenance time (matching backend logic)
    if (cycleMinutes === 2) {
      // Double Dragon: if >= endMaintenanceHour
      if (hourKST >= endMaintenanceHourKST) {
        round = round - maintenanceRounds;
      }
    } else {
      // Space8 and others: if > endMaintenanceHour or (== endMaintenanceHour && minute > 0)
      if (hourKST > endMaintenanceHourKST || (hourKST === endMaintenanceHourKST && minuteKST > 0)) {
        round = round - maintenanceRounds;
      }
    }

    // Handle round == 0 case (matching backend)
    if (round === 0) {
      round = maxRound;
    }

    // Calculate next round and current round
    let nextRound = round + 1;
    if (nextRound > maxRound) {
      nextRound = 1;
    }

    let currentRound = round;

    // Calculate remain time for next round
    const nextRoundTimeSeconds = nextRound * cycleMinutes * 60;
    let remainTimeSeconds = nextRoundTimeSeconds - timeSeconds;
    if (remainTimeSeconds < 0) {
      remainTimeSeconds += maxRound * cycleMinutes * 60;
    }

    const remainMinutes = Math.floor(remainTimeSeconds / 60);
    const remainSeconds = remainTimeSeconds % 60;
    const remainTime = `${remainMinutes}:${remainSeconds < 10 ? '0' : ''}${remainSeconds}`;

    return {
      nextRound: nextRound,
      currentRound: currentRound,
      remainTime: remainTime
    };
  }

  /**
   * Get round from dist.json API via challenger-api
   * @param {string} gameName - Game name
   * @returns {Promise<{currentRound: number, nextRound: number, remainTime: string}>|null}
   */
  async getRoundFromAPI(gameName) {
    if (!this.time) {
      await this.setTime();
    }

    try {
      const response = await axios.get(`${Url.named}lottery/dist`, {
        params: {
          game_type: gameName
        },
        cache: false,
        timeout: 5000
      });

      const result = response.data.data || response.data;

      const round = result.date_round || result.round;
      
      if (!round) {
        return null;
      }

      // Calculate next round
      const maxRoundMap = {
        // Runningball games
        'double_dragon': 660,
        'space8': 440,
        'runningball5_6': 264,
        'runningball5_4': 264,
        'runningball3': 264,
        // Powerball games
        'powerball5': 288,
        'powerball3': 480,
        'powerball': 288, // redball powerball
        // Powerladder games
        'powerladder5': 288,
        'powerladder3': 480,
        'powerladder': 288, // redball powerladder
        // Dari games
        'ladder': 288,
        'daridari': 480,
        'speedladder': 1440,
      };
      const maxRound = maxRoundMap[gameName] || 288;
      
      let nextRound = round + 1;
      if (nextRound > maxRound) {
        nextRound = 1;
      }

      // Calculate remain time based on cycle minutes
      const cycleMinutesMap = {
        // Runningball games
        'double_dragon': 2,
        'space8': 3,
        'runningball5_6': 5,
        'runningball5_4': 5,
        'runningball3': 5,
        // Powerball games
        'powerball5': 5,
        'powerball3': 3,
        'powerball': 5, // redball powerball
        // Powerladder games
        'powerladder5': 5,
        'powerladder3': 3,
        'powerladder': 5, // redball powerladder
        // Dari games
        'ladder': 5,
        'daridari': 3,
        'speedladder': 1,
      };
      const cycleMinutes = cycleMinutesMap[gameName] || 5;
      
      const nowKST = new Date(this.time);
      const hourKST = nowKST.getHours();
      const minuteKST = nowKST.getMinutes();
      const secondKST = nowKST.getSeconds();
      const timeSeconds = (hourKST * 3600) + (minuteKST * 60) + secondKST;
      
      const nextRoundTimeSeconds = nextRound * cycleMinutes * 60;
      let remainTimeSeconds = nextRoundTimeSeconds - timeSeconds;
      if (remainTimeSeconds < 0) {
        remainTimeSeconds += maxRound * cycleMinutes * 60;
      }

      const remainMinutes = Math.floor(remainTimeSeconds / 60);
      const remainSeconds = remainTimeSeconds % 60;
      const remainTime = `${remainMinutes}:${remainSeconds < 10 ? '0' : ''}${remainSeconds}`;

      return {
        nextRound: nextRound,
        currentRound: round,
        remainTime: remainTime
      };
    } catch (error) {
      // API failed, return null to trigger fallback
      return null;
    }
  }

  async getRunningball6Round() {
    // Try API first, fallback to calculation
    const apiResult = await this.getRoundFromAPI('runningball5_6');
    if (apiResult) {
      return apiResult;
    }
    // Fallback: 5min game, maxRound 264, maintenance rounds 24
    return this.getRunningballRound(5, 264, 24);
  }
  
  async getRunningball4Round() {
    // Try API first, fallback to calculation
    const apiResult = await this.getRoundFromAPI('runningball5_4');
    if (apiResult) {
      return apiResult;
    }
    // Fallback: 5min game, maxRound 264, maintenance rounds 24
    return this.getRunningballRound(5, 264, 24);
  }
  
  async getSpace8Round() {
    // Try API first, fallback to calculation
    const apiResult = await this.getRoundFromAPI('space8');
    if (apiResult) {
      return apiResult;
    }
    // Fallback: 3min game, maxRound 440, maintenance rounds 40
    return this.getRunningballRound(3, 440, 40);
  }

  async getDoubleDragonRound() {
    // Try API first, fallback to calculation
    const apiResult = await this.getRoundFromAPI('double_dragon');
    if (apiResult) {
      return apiResult;
    }
    // Fallback: 2min game, maxRound 660, maintenance rounds 60
    return this.getRunningballRound(2, 660, 60);
  }

  async getRunningball2Round() {
    // Try API first, fallback to calculation
    const apiResult = await this.getRoundFromAPI('runningball3');
    if (apiResult) {
      return apiResult;
    }
    // Fallback: 5min game (maze2/speed4), maxRound 264, maintenance rounds 24
    return this.getRunningballRound(5, 264, 24);
  }

  // Powerball games - get from API
  async getPower5MinRound() {
    // Try API first, fallback to calculation
    const apiResult = await this.getRoundFromAPI('powerball5');
    if (apiResult) {
      return apiResult;
    }
    // Fallback: use existing calculation
    if (!this.time) {
      await this.setTime();
    }
    const baseTime = new Date(this.time);
    const hourKST = baseTime.getHours();
    const minuteKST = baseTime.getMinutes();
    const secondKST = baseTime.getSeconds();
    const timeSeconds = (hourKST * 3600) + (minuteKST * 60) + secondKST;
    const round = Math.ceil(timeSeconds / (60 * 5));
    const currentRound = round === 0 ? 288 : round;
    const nextRound = currentRound + 1 > 288 ? 1 : currentRound + 1;
    const nextRoundTimeSeconds = nextRound * 5 * 60;
    let remainTimeSeconds = nextRoundTimeSeconds - timeSeconds;
    if (remainTimeSeconds < 0) {
      remainTimeSeconds += 288 * 5 * 60;
    }
    const remainMinutes = Math.floor(remainTimeSeconds / 60);
    const remainSeconds = remainTimeSeconds % 60;
    const remainTime = `${remainMinutes}:${remainSeconds < 10 ? '0' : ''}${remainSeconds}`;
    return {
      nextRound: nextRound,
      currentRound: currentRound,
      remainTime: remainTime
    };
  }

  async getPower3MinRound() {
    // Try API first, fallback to calculation
    const apiResult = await this.getRoundFromAPI('powerball3');
    if (apiResult) {
      return apiResult;
    }
    // Fallback: use existing calculation
    if (!this.time) {
      await this.setTime();
    }
    const baseTime = new Date(this.time);
    const hourKST = baseTime.getHours();
    const minuteKST = baseTime.getMinutes();
    const secondKST = baseTime.getSeconds();
    const timeSeconds = (hourKST * 3600) + (minuteKST * 60) + secondKST;
    const round = Math.ceil(timeSeconds / (60 * 3));
    const currentRound = round === 0 ? 480 : round;
    const nextRound = currentRound + 1 > 480 ? 1 : currentRound + 1;
    const nextRoundTimeSeconds = nextRound * 3 * 60;
    let remainTimeSeconds = nextRoundTimeSeconds - timeSeconds;
    if (remainTimeSeconds < 0) {
      remainTimeSeconds += 480 * 3 * 60;
    }
    const remainMinutes = Math.floor(remainTimeSeconds / 60);
    const remainSeconds = remainTimeSeconds % 60;
    const remainTime = `${remainMinutes}:${remainSeconds < 10 ? '0' : ''}${remainSeconds}`;
    return {
      nextRound: nextRound,
      currentRound: currentRound,
      remainTime: remainTime
    };
  }

  async getRedBallRound5min() {
    // Try API first, fallback to calculation
    const apiResult = await this.getRoundFromAPI('powerball');
    if (apiResult) {
      return apiResult;
    }
    // Fallback: use existing calculation
    if (!this.time) {
      await this.setTime();
    }
    const baseTime = new Date(this.time);
    const hourKST = baseTime.getHours();
    const minuteKST = baseTime.getMinutes();
    const secondKST = baseTime.getSeconds();
    const timeSeconds = (hourKST * 3600) + (minuteKST * 60) + secondKST + 180;
    let round = Math.ceil(timeSeconds / (60 * 5));
    round = round > 288 ? (round % 288) : round;
    const currentRound = round === 0 ? 288 : round;
    const nextRound = currentRound + 1 > 288 ? 1 : currentRound + 1;
    const nextRoundTimeSeconds = nextRound * 5 * 60;
    let remainTimeSeconds = nextRoundTimeSeconds - (timeSeconds - 180);
    if (remainTimeSeconds < 0) {
      remainTimeSeconds += 288 * 5 * 60;
    }
    const remainMinutes = Math.floor(remainTimeSeconds / 60);
    const remainSeconds = remainTimeSeconds % 60;
    const remainTime = `${remainMinutes}:${remainSeconds < 10 ? '0' : ''}${remainSeconds}`;
    return {
      nextRound: nextRound,
      currentRound: currentRound,
      remainTime: remainTime
    };
  }

  // Dari games - get from API
  async getLadderRound() {
    // Try API first, fallback to calculation
    const apiResult = await this.getRoundFromAPI('ladder');
    if (apiResult) {
      return apiResult;
    }
    // Fallback: 5min game
    return this.get5minRound();
  }

  async getDaridariRound() {
    // Try API first, fallback to calculation
    const apiResult = await this.getRoundFromAPI('daridari');
    if (apiResult) {
      return apiResult;
    }
    // Fallback: 3min game
    return this.get3minRound();
  }

  async getSpeedLadderRound() {
    // Try API first, fallback to calculation
    const apiResult = await this.getRoundFromAPI('speedladder');
    if (apiResult) {
      return apiResult;
    }
    // Fallback: 1min game
    return this.get1minRound();
  }

  /**
   * 파워계열 회차 조회
   * @returns {Promise<*>}
   */
  async getPowerRound() {
    return this.getGameRound(125, 300, 288);
  }

  async getThreeMinRound() {
    return this.getGameRound(125, 180, 480);
  }


  /**
   * 키노계열 회차 조회
   * @returns {*}
   */
  async getKenoRound() {
    return this.getGameRound(30, 300, 288);
  }

  /**
   * 파워키노계열 회차 조회
   * @returns {null|{currentRound: number, nextRound: number}}
   */
  async getPowerKenoRound() {
    const maxRound = 576;
    const powerRound = await this.getPowerRound();
    const kenoRound = await this.getKenoRound();

    let nextRound = powerRound.nextRound + kenoRound.nextRound - 1;
    nextRound = maxRound < nextRound ? 1 : nextRound;

    let currentRound = nextRound - 1;
    currentRound = currentRound === 0 ? maxRound : currentRound;

    return {
      nextRound: nextRound,
      currentRound: currentRound,
      remainTime:
        nextRound % 2 === 0 ? kenoRound.remainTime : powerRound.remainTime
    };
  }

  /**
   * 1분 게임 회차 조회
   * @returns {*}
   */
  async get1minRound() {
    return this.getGameRound(0, 60, 1440);
  }

  /**
   * 2분 게임 회차 조회
   * @returns {*}
   */
  async get2minRound() {
    return this.getGameRound(0, 120, 720);
  }

  /**
   * 3분 게임 회차 조회
   * @returns {*}
   */
  async get3minRound() {
    return this.getGameRound(0, 180, 480);
  }

  /**
   * 5분 게임 회차 조회
   * @returns {*}
   */
  async get5minRound() {
    return this.getGameRound(0, 300, 288);
  }

  /**
   * 레드볼 회차 조회
   */
  async getRedballRound() {
    return this.getGameRound(179, 300, 288);
  }

  /**
   * 모든 게임 회차 조회
   * @returns {Promise<{speedLadder: *, kenoLadder: *, speedkeno: *, powerball: *, ladder: *, daridari: *, powerLadder: *, snailRacing: *, powerKenoLadder: *}>}
   */
  async getAllGameRound() {
    clearTimeout(this.autoStopTimer);
    this.autoStopTimer = setTimeout(this.stop, 1000 * 10);

    this.useTimer = true;
    return {
      // speedladder: await this.get1minRound(),
      // daridari: await this.get3minRound(),
      // ladder: await this.get5minRound(),
      // snailracing: await this.get3minRound(),
      powerball: await this.getKenoRound(),
      three_powerball: await this.get3minRound(),
      powerladder: await this.getKenoRound(),
      three_powerladder: await this.get3minRound(),
      dh_powerball:await this.getKenoRound(),
      dh_speedkeno:await this.getKenoRound(),
      dh_kenoladder:await this.getKenoRound(),
      redball_powerball:await this.getRedballRound(),
      redball_powerladder:await this.getRedballRound()
      // speedkeno: await this.getKenoRound(),
      // kenoladder: await this.getKenoRound(),
      // powerkenoladder: await this.getPowerKenoRound()
    };

    this.plusSecond = -3;
  }



  async getHoldemRound() {
    clearTimeout(this.autoStopTimer);
    this.autoStopTimer = setTimeout(this.stop, 1000 * 10);

    this.useTimer = true;
    this.plusSecond = -3;

    return await this.get2minRound();
  }
}

export default ClockStore;
